import { getCommentsBySpecificTable } from "service/comment.service";

export const ASSESSMENT_START_TABLE_ID = "assessment_start_DAPT";
export const DPAT_COMMENT_TYPE = "DPAT";

const LOCKED_ASSESSMENT_STATUSES = ["Start", "Pending", "Completed", "Closed"];

/** True once an assessment has been started for this district/year (use CMS snapshot, not DHIS2). */
export function isDpatAssessmentLocked(status) {
  return LOCKED_ASSESSMENT_STATUSES.includes(status);
}

export async function fetchAssessmentStartTables(districtId, year) {
  const response = await getCommentsBySpecificTable(
    districtId,
    year,
    DPAT_COMMENT_TYPE,
    ASSESSMENT_START_TABLE_ID,
  );
  const payload = response?.data ?? response;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : payload && typeof payload === "object"
        ? [payload]
        : [];
  const comment = rows[0] || null;
  return comment?.dddpData?.tables ?? comment?.tables ?? null;
}

/** Full snapshot entry for one indicator (e.g. tables.waterServices). */
export function getAssessmentTableEntry(tables, key) {
  if (!tables || !key) return null;
  return tables[key] ?? null;
}

/**
 * Unwraps nested `.data` when the saved snapshot stores display fields inside it
 * (meetings, fulfillment, score fields, etc.).
 */
export function getDisplayDataFromTableEntry(entry) {
  if (!entry) return null;
  const inner = entry.data;
  if (
    inner &&
    typeof inner === "object" &&
    (inner.fulfillment != null ||
      inner.meetings ||
      inner.data ||
      inner.aapApprovalLink ||
      Array.isArray(inner.data))
  ) {
    return inner;
  }
  return entry;
}

/** @deprecated Use getAssessmentTableEntry + getDisplayDataFromTableEntry in the component. */
export function getMeetingStyleSlice(tables, ...keys) {
  for (const key of keys) {
    const entry = getAssessmentTableEntry(tables, key);
    const display = getDisplayDataFromTableEntry(entry);
    if (display) return display;
  }
  return null;
}

export function getGaMeetingSlice(tables) {
  return getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "gaMeeting"));
}

export function getAapBudgetApprovalSlice(tables) {
  return getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "aapBudgetApproval"));
}

export function getExecutiveCommitteeSlice(tables) {
  return (
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "executiveCommitteeMember")) ??
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "ecaMeetingData"))
  );
}

export function getManagementMeetingSlice(tables) {
  return (
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "managementMeeting")) ??
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "managementMeetingsData"))
  );
}

export function getPrccMeetingSlice(tables) {
  return (
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "prccMeeting")) ??
    getDisplayDataFromTableEntry(getAssessmentTableEntry(tables, "prccMeetingData"))
  );
}

/**
 * Meeting-style indicators: live `data` is { fulfillment, data: rows }.
 * CMS stores getData() as { indicator, area, data: { fulfillment, data } }.
 */
export function resolveMeetingDataProp({ isAssessmentStarted, savedAssessmentData, liveData }) {
  if (!isAssessmentStarted) return liveData;
  if (!savedAssessmentData) return null;
  if (
    savedAssessmentData.data &&
    (savedAssessmentData.data.fulfillment != null || savedAssessmentData.data.data)
  ) {
    return savedAssessmentData.data;
  }
  return getDisplayDataFromTableEntry(savedAssessmentData);
}

/** GA / similar: live shape is { meetings, fulfillment, numberOfDecision }. */
export function resolveGaMeetingDataProp({ isAssessmentStarted, savedAssessmentData, liveData }) {
  if (!isAssessmentStarted) return liveData;
  return getDisplayDataFromTableEntry(savedAssessmentData) ?? null;
}

/** SDI2 service-provider indicators (water, electricity, …) — snapshot matches getData(). */
export function hydrateSdi2ServiceProviderSnapshot(snap, setters) {
  if (!snap) return;
  setters.setProviders?.(snap.waterProvidersData ?? []);
  setters.setImprovement?.(snap.waterImprovement ?? []);
  setters.setLinks?.(snap.links ?? []);
  setters.setScoreI?.(snap.scorei ?? 0);
  setters.setScoreII?.(snap.scoreii ?? 0);
  setters.setFirstPercentage?.(snap.firstPercentage ?? "0.00");
  setters.setMaxScore?.(snap.maxScore ?? 3);
}

export function getSubStructureCommitteeSlice(tables) {
  const entry =
    getAssessmentTableEntry(tables, "subStructureCommitteeMeeting") ??
    getAssessmentTableEntry(tables, "subStatutoryData");
  if (!entry) {
    return {
      meetingData: null,
      departments: tables?.subCommitteeDepartments ?? null,
      members: tables?.subCommitteeMembers ?? null,
    };
  }
  return {
    meetingData: getDisplayDataFromTableEntry(entry),
    departments: entry.departments ?? tables?.subCommitteeDepartments ?? null,
    members: entry.members ?? tables?.subCommitteeMembers ?? null,
  };
}
